import { watch, type Directive, type DirectiveBinding, type WatchStopHandle } from 'vue';
import itemRankingDescriptionService from '@/services/itemRankingDescription.service';
import { isItemConfigured, isItemRankingVisible, openItemRankingConfigModal, useItemRanking } from '@/composables/itemRanking';

// Port a Vue de la directiva AngularJS item-ranking (src/frontend/commons/directives/itemRanking/
// itemRanking.directive.js): oculta/muestra el elemento según la jerarquización del item para el
// profile actual, y para sysadmin agrega el ícono de configuración + ctrl/cmd+click que abre el
// modal de edición. No se porta item-index ni la integración con md-tabs (no existen en Vue).
//
// Uso: v-item-ranking="'mobile.leftmenu.turnos'" (visible por default)
//      v-item-ranking.hidden="'mobile.leftmenu.turnos'" (oculto por default, como item-hidden="true")

// listeners de click agregados por la directiva, para poder removerlos en unmounted
const clickHandlers = new WeakMap<HTMLElement, (event: MouseEvent) => void>();

// watchers de visibilidad agregados por la directiva, para poder detenerlos en unmounted
const visibilityWatchStops = new WeakMap<HTMLElement, WatchStopHandle>();

// aplica al elemento la clase/visibilidad que corresponda según si es visible y si es sysadmin
async function applyItemRanking(el: HTMLElement, itemName: string, itemDefault: boolean) {
  const { visible, isSysadmin } = useItemRanking(itemName, itemDefault);

  if (!isSysadmin) {
    // visible.value resuelve sincrónicamente con el sistema nuevo, pero con el legacy se
    // actualiza async cuando resuelve loadLegacyItemRankings: observarlo (en vez de leerlo una
    // sola vez) garantiza que el DOM se actualice también en ese caso
    const stop = watch(visible, isVisible => {
      if (!isVisible) el.style.display = 'none';
      else el.style.removeProperty('display');
    }, { immediate: true });
    visibilityWatchStops.set(el, stop);
    return;
  }

  el.classList.add('itemRanking');
  const allDescriptions = await itemRankingDescriptionService.queryNoPaginate({ rankingItem: itemName });
  if (!document.contains(el)) return; // elemento desmontado durante el fetch, nada que hacer
  updateSysadminClasses(el, itemName, itemDefault, allDescriptions);

  const onClick = (event: MouseEvent) => {
    if (!event.ctrlKey && !event.metaKey) return;
    // el listener es de captura, así que los ancestros jerarquizados se disparan antes que el elemento
    // clickeado: solo actúa el item-ranking más interno (el más cercano al target con la clase 'itemRanking')
    if ((event.target as Element | null)?.closest('.itemRanking') !== el) return;
    event.stopPropagation();
    event.preventDefault();
    openItemRankingConfigModal(itemName, itemDefault)
      .then(() => itemRankingDescriptionService.queryNoPaginate({ rankingItem: itemName }))
      .then(descriptions => updateSysadminClasses(el, itemName, itemDefault, descriptions))
      .catch(err => console.error(err));
  };
  el.addEventListener('click', onClick, true);
  clickHandlers.set(el, onClick);
}

// recalcula y reaplica las clases de configuración (ícono de engranaje) para sysadmin
function updateSysadminClasses(el: HTMLElement, itemName: string, itemDefault: boolean, allDescriptions: any[]) {
  el.classList.remove('itemRankingConfig', 'itemRankingHidden', 'itemRankingHiddenDefault');
  const notVisible = !isItemRankingVisible(itemName, itemDefault);
  if (isItemConfigured(itemName, allDescriptions)) {
    el.classList.add(notVisible ? 'itemRankingHidden' : 'itemRankingConfig');
  } else if (!itemDefault) {
    el.classList.add('itemRankingHiddenDefault');
  }
  const preferences = window.profile?.userId && localStorage[`${window.profile.userId}_preferences`];
  let showRankingConfigs = false;
  try {
    showRankingConfigs = preferences && JSON.parse(preferences).showRankingConfigs;
  } catch { /* localStorage corrupto, ignorar */ }
  el.classList.toggle('hideRankingConfigs', !showRankingConfigs);
}

function mounted(el: HTMLElement, binding: DirectiveBinding<string>) {
  const itemName = binding.value;
  if (!itemName || !window.profile) return;
  const itemDefault = !binding.modifiers.hidden;
  applyItemRanking(el, itemName, itemDefault).catch(err => console.error(err));
}

function unmounted(el: HTMLElement) {
  const onClick = clickHandlers.get(el);
  if (onClick) {
    el.removeEventListener('click', onClick, true);
    clickHandlers.delete(el);
  }
  const stopWatch = visibilityWatchStops.get(el);
  if (stopWatch) {
    stopWatch();
    visibilityWatchStops.delete(el);
  }
}

export const itemRankingDirective: Directive<HTMLElement, string> = { mounted, unmounted };
